import * as React from "react";
import * as moment from "moment";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { Pager } from "../utils/Pager";
import { Button, Classes, Dialog, FormGroup, InputGroup, TextArea, Tag } from "@blueprintjs/core";
import { AppStore } from "../../stores/AppStore";
import { toast } from "react-toastify";
import { SugestoesMelhoriasStore } from "../../stores/sugestoes-melhorias/SugestoesMelhoriasStore";


@observer
export default class SugestoesMelhoriasPage extends React.Component<{}, {}> {

  obs = observable({
    melhorias_page: 1 as number,
    titulo: "" as string,
    descricao: "" as string,
    imagens: [] as any[],
    drag_over: false as boolean,
    loading_envio: false as boolean,
    melhoria_selecionada: null as any,
    open_form: false as boolean
  });

  readonly TAG_STYLE: React.CSSProperties = {
    width: "70%",
    textAlign: "center"
  };

  /**
  * Referência do input de arquivo oculto.
  *
  * O input nativo fica escondido na tela e é acionado pelo clique na área de
  * upload, evitando o visual padrão do navegador e mantendo o padrão de botões
  * do sistema.
  *
  * @type {HTMLInputElement | null}
  */

  inputFile: HTMLInputElement | null = null;

  /**
  * Quantidade máxima de imagens permitidas por sugestão.
  * @readonly
  * @type {number}
  */

  readonly MAX_IMAGENS = 5;

  /**
  * Tamanho máximo permitido por imagem, em megabytes.
  * @readonly
  * @type {number}
  */

  readonly MAX_MB = 5;

  /**
  * Tamanho mínimo permitido para a descrição, em caracteres.
  * @readonly
  * @type {number}
  */

  readonly MIN_DESCRICAO = 50;

  /**
  * Tamanho mínimo permitido para o título, em caracteres.
  * @readonly
  * @type {number}
  */

  readonly MIN_TITULO = 10;

  /**
  * Valor padrão de cor para status caso venha como null.
  * @readonly
  */

  readonly COLOR_STATUS_PADRAO = "#166470";

  /**
  * Inicializa o componente com as propriedades recebidas.
  *
  * @param {any} props Propriedades recebidas pelo componente.
  * @returns {void} Não retorna valor.
  */

  constructor(props: any) {
    super(props);
  }

  /**
  * Executa ações iniciais após a montagem do componente.
  *
  * Define o título da página através da `AppStore` e carrega a primeira página
  * da listagem de sugestões abertas pelo usuário logado.
  *
  * @returns {void} Não retorna valor.
  */

  async componentDidMount() {
    AppStore.setTitle("Sugestões de Melhorias");
    await SugestoesMelhoriasStore.handleConsultaStatus();
    this.handleBuscaMelhorias(1);
  }

  /**
  * Libera as URLs temporárias das imagens em pré-visualização.
  *
  * As URLs criadas por `URL.createObjectURL` permanecem em memória até serem
  * revogadas. A limpeza no unmount evita vazamento de memória quando o usuário
  * sai da tela sem enviar a sugestão.
  *
  * @returns {void} Não retorna valor.
  */

  componentWillUnmount() {
    this.obs.imagens.forEach((img: any) => URL.revokeObjectURL(img.url));
  }

  /**
  * Executa a busca das sugestões de melhoria abertas pelo usuário.
  *
  * Chama a store responsável por consultar as sugestões, utilizando a página
  * informada e limite fixo de registros por página.
  *
  * @async
  * @param {number} page Página que será consultada na listagem.
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  handleBuscaMelhorias = async (page: number) => {
    this.obs.melhorias_page = page;
    await SugestoesMelhoriasStore.handleConsultaMelhoriasUsuario(page, 10);
  };

  /**
  * Abre o modal de cadastro de uma nova sugestão.
  *
  * O formulário deixou de ocupar a coluna lateral da tela para liberar a
  * largura inteira à listagem, e passou a ser acionado sob demanda por este
  * método.
  *
  * @returns {void} Não retorna valor.
  */

  handleOpenForm = () => {
    this.obs.melhoria_selecionada = null;
    this.obs.open_form = true;
  };

  /**
  * Fecha o modal de cadastro sem descartar o que já foi digitado.
  *
  * O conteúdo é preservado propositalmente: fechar o modal por engano no meio
  * do preenchimento não pode custar o texto e os anexos do usuário. Para zerar
  * os campos existe o botão "Limpar".
  *
  * @returns {void} Não retorna valor.
  */

  handleCloseForm = () => {
    this.obs.imagens = [];
    this.obs.titulo = "";
    this.obs.descricao = "";
    this.obs.melhoria_selecionada = null;
    this.obs.open_form = false;
  };

  /**
  * Abre o seletor de arquivos do navegador.
  *
  * Aciona programaticamente o input de arquivo oculto, mantendo a área de
  * upload customizada como único elemento visível ao usuário.
  *
  * @returns {void} Não retorna valor.
  */

  handleOpenSeletor = () => {
    if (this.inputFile) {
      this.inputFile.click();
    }
  };

  /**
  * Valida e adiciona imagens à lista de anexos da sugestão.
  *
  * Percorre os arquivos recebidos, descartando os que não são imagens ou que
  * excedem o tamanho máximo permitido, e respeita o limite total de anexos.
  * Para cada arquivo aceito é gerada uma URL temporária de pré-visualização.
  *
  * O arquivo original é mantido em memória para ser enviado posteriormente via
  * `FormData` no momento do envio da sugestão.
  *
  * @param {FileList | null} fileList Lista de arquivos selecionados ou arrastados pelo usuário.
  * @returns {void} Não retorna valor.
  */

  handleAddImagens = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const arquivos = Array.prototype.slice.call(fileList);

    arquivos.forEach((file: File) => {
      if (this.obs.imagens.length >= this.MAX_IMAGENS) {
        return toast.warning(`É possível anexar no máximo ${this.MAX_IMAGENS} arquivos.`);
      }

      const extensao = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

      const extensoesPermitidas = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xlsx", ".xls", ".xlsm", ".csv"];

      if (extensoesPermitidas.indexOf(extensao) === -1) {
        return toast.warning(`"${file.name}" não é um arquivo permitido. Envie JPG, PNG, PDF, DOC, DOCX, XLSX, XLS, XLSM ou CSV.`);
      }

      if (file.size > this.MAX_MB * 1024 * 1024) {
        return toast.warning(`"${file.name}" excede o limite de ${this.MAX_MB} MB.`);
      }

      this.obs.imagens.push({
        id: `${file.name}-${file.size}-${Date.now()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        extensao: extensao,
        url: URL.createObjectURL(file),
        file: file
      });
    });
  };

  /**
  * Remove uma imagem da lista de anexos.
  *
  * Revoga a URL temporária de pré-visualização antes de retirar o item da
  * lista, liberando o recurso alocado pelo navegador.
  *
  * @param {string} id Identificador local da imagem que será removida.
  * @returns {void} Não retorna valor.
  */

  handleRemoveImagem = (id: string) => {
    const imagem = this.obs.imagens.find((img: any) => img.id === id);

    if (imagem) {
      URL.revokeObjectURL(imagem.url);
    }

    this.obs.imagens = this.obs.imagens.filter((img: any) => img.id !== id);
  };

  /**
  * Trata o evento de soltar arquivos sobre a área de upload.
  *
  * Cancela o comportamento padrão do navegador (que abriria o arquivo em uma
  * nova aba), desativa o destaque visual da área e encaminha os arquivos para
  * a validação.
  *
  * @param {any} e Evento de drop disparado pela área de upload.
  * @returns {void} Não retorna valor.
  */

  handleDrop = (e: any) => {
    e.preventDefault();
    this.obs.drag_over = false;
    this.handleAddImagens(e.dataTransfer.files);
  };

  /**
  * Limpa os campos do formulário de sugestão.
  *
  * Revoga as URLs de pré-visualização das imagens anexadas e redefine título,
  * descrição e lista de anexos.
  *
  * @returns {void} Não retorna valor.
  */

  handleLimparFormulario = () => {
    this.obs.imagens.forEach((img: any) => URL.revokeObjectURL(img.url));
    this.obs.imagens = [];
    this.obs.titulo = "";
    this.obs.descricao = "";
  };

  /**
  * Envia a sugestão de melhoria preenchida pelo usuário.
  *
  * Valida o preenchimento do título, monta um `FormData` com os campos de texto
  * e as imagens anexadas e envia para a store responsável pelo cadastro.
  *
  * O envio utiliza `multipart/form-data` porque a requisição carrega arquivos
  * binários junto dos campos de texto.
  *
  * Em caso de sucesso, limpa o formulário, fecha o modal e recarrega a listagem
  * para que a nova sugestão apareça imediatamente. O modal só fecha no caminho
  * de sucesso: se a validação ou a requisição falharem, o usuário permanece no
  * formulário com o que já havia digitado.
  *
  * @async
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  handleEnviarMelhoria = async () => {

    const titulo = this.obs.titulo.trim();
    const descricao = this.obs.descricao.trim();

    if (!titulo) {
      return toast.warning("É necessário informar um título para a sugestão!");
    }

    if (titulo.length < this.MIN_TITULO) {
      return toast.warning(`O título precisa ter ao menos ${this.MIN_TITULO} caracteres.`);
    }

    if (!descricao) {
      return toast.warning("É necessário informar os detalhes para a sugestão!");
    }

    if (descricao.length < this.MIN_DESCRICAO) {
      return toast.warning(`Os detalhes precisam ter ao menos ${this.MIN_DESCRICAO} caracteres.`);
    }

    try {
      this.obs.loading_envio = true;

      const form = new FormData();
      form.append("titulo", titulo);
      form.append("descricao", descricao);

      this.obs.imagens.forEach((img: any) => {
        form.append("imagens[]", img.file, img.name);
      });

      await SugestoesMelhoriasStore.handleCreateMelhoria(form);

      this.handleLimparFormulario();
      this.handleCloseForm();
      await this.handleBuscaMelhorias(1);

    } catch (error) {

      toast.error("Não foi possível enviar a sugestão.");

    } finally {
      this.obs.loading_envio = false;
    }
  };

  /**
  * Abre o modal de detalhe de uma sugestão já enviada.
  *
  * Armazena a linha selecionada na listagem e habilita a exibição do modal,
  * que apresenta descrição completa, status e imagens anexadas.
  *
  * @param {any} row Objeto da sugestão selecionada na listagem.
  * @returns {void} Não retorna valor.
  */

  handleOpenDetalhe = async (row: any) => {

    const detalhe = await SugestoesMelhoriasStore.handleConsultaDetalhe(row._id);

    if (!detalhe) {
      return;
    }

    this.obs.melhoria_selecionada = detalhe;
    this.obs.open_form = true;

  };

  render() {

    function dataPrevistaFormatter(cell, row) {
      if (!cell) {
        return "Não definida";
      }
      return moment.utc(cell).format("DD/MM/YYYY");
    }

    function dateFormatter(cell, row) {
      return moment(cell).add(3, "hour").format("DD/MM/YYYY") + " às " + moment(cell).add(3, "hour").format("HH:mm");
    };

    const tag_style = this.TAG_STYLE;

    function statusFormatter(cell, row) {
      const status = SugestoesMelhoriasStore.obs.status_sugestao.find((s: any) => Number(s.status) === Number(cell));

      if (!status) {
        return (
          <Tag
            className="tagConfirmacao"
            style={{ ...tag_style, background: "#166470", borderColor: "#166470", color: "#fff" }}
          >
            {"NÃO DISPONÍVEL"}
          </Tag>
        );
      }

      const nome = status.nome || "";
      const label = nome.charAt(0).toUpperCase() + nome.slice(1);
      const cor = status.color || "#166470";

      return (
        <Tag
          className="tagConfirmacao"
          style={{ ...tag_style, background: cor, borderColor: cor, color: "#fff" }}
        >
          {label}
        </Tag>
      );
    }

    function statusFormatterDetalhe(cell, row) {
      const status = SugestoesMelhoriasStore.obs.status_sugestao.find((s: any) => Number(s.status) === Number(cell));

      const cor = (status && status.color) || "#166470";
      const nome = (status && status.nome) || "";
      const label = nome ? nome.charAt(0).toUpperCase() + nome.slice(1) : "NÃO DISPONÍVEL";

      return (
        <Tag
          className="tagConfirmacao"
          style={{ background: cor, borderColor: cor, color: "#fff", fontSize: 11, padding: "1px 10px", minWidth: 0 }}
        >
          {label}
        </Tag>
      );
    }

    const detalhe = this.obs.melhoria_selecionada;
    const modo_detalhe = !!detalhe;

    return (
      <>

        <MDBRow>
          <MDBCol sm="12" md="12" lg="12" xl="12">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10
              }}
            >
              <MDBBtn
                className="btnDefault btnBuscarNfe"
                onClick={this.handleOpenForm}
              >
                Nova sugestão
              </MDBBtn>

            </div>

            <div data-tut="table">
              <BootstrapTable
                className="TableDefault"
                ref="table"
                data={SugestoesMelhoriasStore.obs.melhorias_usuario}
              >
                <TableHeaderColumn dataField="_id" isKey={true} hidden> # </TableHeaderColumn>
                <TableHeaderColumn dataField="titulo" width="42%">Título</TableHeaderColumn>
                <TableHeaderColumn dataFormat={dateFormatter} dataField="inserted_at" width="18%">Data</TableHeaderColumn>
                <TableHeaderColumn dataFormat={dateFormatter} dataField="updated_at" width="16%"> Atualizado em </TableHeaderColumn>
                <TableHeaderColumn dataFormat={dataPrevistaFormatter} dataField="data_prevista" width="16%"> Data prevista </TableHeaderColumn>
                <TableHeaderColumn dataFormat={statusFormatter} dataField="status" width="14%">Status</TableHeaderColumn>
                <TableHeaderColumn
                  dataField="visualizar"
                  width="7%"
                  dataFormat={(cell, row) => (
                    <MDBBtn
                      color="default"
                      rounded
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.handleOpenDetalhe(row);
                      }}
                    >
                      <img src="/images/view.png" width="18px" alt="Visualizar" />
                    </MDBBtn>
                  )}
                  thStyle={{ textAlign: "center" }}
                  tdStyle={{ textAlign: "center" }}
                >
                  Detalhes
                </TableHeaderColumn>
              </BootstrapTable>
            </div>

            <div style={{ marginTop: 5, float: "right", marginBottom: 60 }}>
              <Pager
                currentPage={this.obs.melhorias_page}
                totalCount={SugestoesMelhoriasStore.obs.melhorias_length}
                pageSize={10}
                onPageChange={page => this.handleBuscaMelhorias(page)}
              />
            </div>
          </MDBCol>
        </MDBRow>

        <Dialog
          isOpen={this.obs.open_form}
          onClose={this.handleCloseForm}
          title={modo_detalhe ? "Detalhe da sugestão" : "Nova sugestão"}
          canOutsideClickClose={modo_detalhe}
          canEscapeKeyClose={true}
          style={{ width: 620, maxWidth: "94vw" }}
        >

          <div className={Classes.DIALOG_BODY} style={{ maxHeight: "68vh", overflow: "auto" }}>

            {modo_detalhe && (
              <div className="sugestao-detalhe-meta">
                {statusFormatterDetalhe(detalhe.status, detalhe)}

                <span className="sugestao-detalhe-data">
                  Enviada em {dateFormatter(detalhe.inserted_at, detalhe)}
                </span>
              </div>
            )}

            {modo_detalhe && (
              <FormGroup label="Data prevista">
                <InputGroup
                  value={detalhe.data_prevista ? moment.utc(detalhe.data_prevista).format("DD/MM/YYYY") : "Ainda não definida"}
                  readOnly
                  disabled
                  fill
                />
              </FormGroup>
            )}

            {modo_detalhe && detalhe.motivo_recusa && detalhe.motivo_recusa.trim() !== "" && (
              <FormGroup label="Motivo da recusa">
                <TextArea
                  value={detalhe.motivo_recusa}
                  rows={4}
                  readOnly
                  disabled
                  fill
                />
              </FormGroup>
            )}

            <FormGroup label="Título">
              <InputGroup
                value={modo_detalhe ? detalhe.titulo : this.obs.titulo}
                placeholder="Informe um título."
                onChange={(e) => (this.obs.titulo = e.target.value)}
                maxLength={100}
                readOnly={modo_detalhe}
                disabled={modo_detalhe}
                fill
              />
            </FormGroup>

            <FormGroup label="Detalhes">
              <TextArea
                placeholder="Informe detalhadamente sua sugestão."
                value={modo_detalhe ? (detalhe.descricao || "") : this.obs.descricao}
                onChange={(e) => (this.obs.descricao = e.target.value.slice(0, 500))}
                rows={5}
                readOnly={modo_detalhe}
                disabled={modo_detalhe}
                fill
              />
            </FormGroup>

            {modo_detalhe ? (

              <FormGroup label="Arquivos">

                {detalhe.arquivos && detalhe.arquivos.length > 0 ? (

                  <div className="sugestao-detalhe-arquivos">

                    {detalhe.arquivos.map((arquivo: any, i: number) => {
                      return (
                        <div
                          title={arquivo.nome}
                          key={arquivo._id || i}
                          className="sugestao-detalhe-arquivo"
                        >
                          <span>
                            {arquivo.nome.length > 20 
                            ? `${arquivo.nome.slice(0, 20)}...`
                            : `${arquivo.nome}`
                          }
                          </span>

                          <Button
                            icon="download"
                            minimal
                            title={`Baixar ${arquivo.nome}`}
                            onClick={() =>
                              SugestoesMelhoriasStore.handleDownloadArquivo(detalhe._id, i, arquivo.nome)
                            }
                          />
                        </div>
                      );
                    })}

                  </div>

                ) : (

                  <p className="sugestao-detalhe-sem-arquivos">
                    Nenhum arquivo anexado.
                  </p>

                )}

              </FormGroup>

            ) : (

              <FormGroup label={`Arquivos (opcional · até ${this.MAX_IMAGENS})`}>
                <div
                  onClick={this.handleOpenSeletor}
                  onDragOver={(e) => {
                    e.preventDefault();
                    this.obs.drag_over = true;
                  }}
                  onDragLeave={() => (this.obs.drag_over = false)}
                  onDrop={this.handleDrop}
                  className={`upload-area ${this.obs.drag_over ? "upload-area-drag-over" : ""}`}
                >
                  <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    Arraste um arquivo ou clique para escolher
                  </p>
                  <p style={{ fontSize: 11, color: "#8a9299", marginBottom: 0 }}>
                    JPG, PNG, PDF, DOC, DOCX, XLSX, XLS, XLSM ou CSV até {this.MAX_MB} MB
                  </p>
                </div>

                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xlsx,.xls,.xlsm,.csv"
                  multiple
                  ref={(ref) => (this.inputFile = ref)}
                  onChange={(e) => { this.handleAddImagens(e.target.files); e.target.value = ""; }}
                  style={{ display: "none" }}
                />

                {this.obs.imagens.length > 0 && (
                  <div className="sugestao-imagens-container">
                    {this.obs.imagens.map((arquivo: any) => {
                      const isImagem = arquivo.type && arquivo.type.indexOf("image/") === 0;

                      return (
                        <div
                          key={arquivo.id}
                          className="sugestao-imagem"
                        >
                          {isImagem ? (
                            <img
                              src={arquivo.url}
                              alt={arquivo.name}
                              title={arquivo.name}
                              className="sugestao-detalhe-imagem"
                            />
                          ) : (
                            <div
                              className="sugestao-arquivo-preview"
                              title={arquivo.name}
                            >
                              {arquivo.name}
                            </div>
                          )}

                          <Button
                            icon="cross"
                            minimal
                            intent="danger"
                            className={`${Classes.SMALL} remove-imagem-sugestao`}
                            onClick={() => this.handleRemoveImagem(arquivo.id)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

              </FormGroup>

            )}

          </div>

          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            {modo_detalhe ? (
              <MDBBtn className="btnDefault btnBuscarNfe" onClick={this.handleCloseForm} style={{ marginRight: 10, float: "right" }}>
                Fechar
              </MDBBtn>
            ) : (
              <>
                <MDBBtn className="btnDefault btnBuscarNfe" color="default" onClick={this.handleCloseForm} style={{ marginRight: 10, float: "right" }}>
                  Cancelar
                </MDBBtn>
                <MDBBtn className="btnDefault btnBuscarNfe" onClick={this.handleEnviarMelhoria} disabled={this.obs.loading_envio} style={{ marginRight: 10, float: "right" }}>
                  {this.obs.loading_envio ? "Enviando..." : "Enviar sugestão"}
                </MDBBtn>
              </>
            )}
          </div>

        </Dialog>

      </>
    );
  }
}